import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contractsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, FileText, UserX, Plus, ShieldCheck, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ContractDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [dashRes, reviewsRes] = await Promise.all([
          contractsAPI.dashboard(),
          contractsAPI.pendingReviews()
        ]);
        setData(dashRes.data);
        setPendingReviews(reviewsRes.data);
      } catch { toast.error('Failed to load dashboard'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;

  const stats = [
    { label: 'Active Customers', value: data.active_customers, icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/contracts/customers' },
    { label: 'Suspended', value: data.suspended_customers, icon: UserX, color: 'text-red-600', bg: 'bg-red-50', link: '/contracts/customers?status=suspended' },
    { label: 'Total POs', value: data.total_purchase_orders, icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', link: '/contracts/purchase-orders' },
    { label: 'Active POs', value: data.active_purchase_orders, icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50', link: '/contracts/purchase-orders?status=active' },
    { label: 'Pending Reviews', value: data.pending_reviews || 0, icon: ShieldCheck, color: 'text-violet-600', bg: 'bg-violet-50', link: '/contracts/purchase-orders' },
  ];

  const REVIEW_BADGES = {
    pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
    open: { label: 'Open', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: AlertCircle },
  };

  return (
    <div className="space-y-6" data-testid="contract-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Contract Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Customers, purchase orders & contract reviews</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigate('/contracts/customers/new')} data-testid="new-customer-btn">
            <Plus className="w-4 h-4 mr-1" /> New Customer
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/contracts/purchase-orders/new')} data-testid="new-po-btn">
            <Plus className="w-4 h-4 mr-1" /> New PO
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map(s => (
          <Card key={s.label} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(s.link)}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending Contract Reviews */}
      {pendingReviews.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-violet-600" />
              <h3 className="text-sm font-semibold">Open Contract Reviews ({pendingReviews.length})</h3>
            </div>
            <div className="space-y-2">
              {pendingReviews.map(po => {
                const badge = REVIEW_BADGES[po.review_status] || REVIEW_BADGES.pending;
                const BadgeIcon = badge.icon;
                return (
                  <div key={po.id}
                    className="flex items-center justify-between p-2.5 rounded border bg-muted/30 cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/contracts/purchase-orders/${po.id}`)}
                    data-testid={`review-item-${po.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-xs font-medium">{po.po_number} - {po.customer_name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          PO Date: {po.po_date} | Value: {po.value ? `Rs ${po.value.toLocaleString()}` : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!po.acknowledgment_sent && (
                        <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200">
                          Ack not sent
                        </Badge>
                      )}
                      <Badge variant="outline" className={`text-[10px] ${badge.color}`}>
                        <BadgeIcon className="w-3 h-3 mr-0.5" />
                        Review: {badge.label}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {data.recent_purchase_orders?.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-3">Recent Purchase Orders</h3>
            <div className="space-y-2">
              {data.recent_purchase_orders.map(po => (
                <div key={po.id} className="flex items-center justify-between p-2 rounded border bg-muted/30 cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/contracts/purchase-orders/${po.id}`)}>
                  <div>
                    <p className="text-xs font-medium">{po.po_number} - {po.customer_name}</p>
                    <p className="text-[10px] text-muted-foreground">Project: {po.project_number || '-'} | {po.po_date}</p>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">{po.status}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
